import { useEffect, useState } from "react";

const confettiColors = [
  "#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEAA7", 
  "#DDA0DD", "#98D8C8", "#F7DC6F", "#BB8FCE", "#85C1E9"
];

const confettiShapes = ["■", "●", "▲", "★", "♦", "❤"];

const ConfettiPiece = ({ delay, color, shape }) => {
  const [style, setStyle] = useState({});

  useEffect(() => {
    const startX = 45 + Math.random() * 10; // Start from center-ish
    const endX = Math.random() * 100;
    const rotation = Math.random() * 720 - 360;
    const duration = 2 + Math.random() * 2;

    setStyle({
      position: "fixed",
      left: `${startX}%`,
      top: "40%",
      fontSize: `${12 + Math.random() * 12}px`,
      color: color,
      pointerEvents: "none",
      zIndex: 9999,
      opacity: 1,
      animation: `confettiFall ${duration}s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}s forwards`,
      "--end-x": `${endX - startX}vw`,
      "--rotation": `${rotation}deg`,
    });
  }, [delay, color]);

  return <div style={style}>{shape}</div>;
};

export default function ConfettiCelebration({ 
  active, 
  onComplete,
  message = "🎉 Great job!",
  subMessage = "",
  duration = 4000 
}) {
  const [pieces, setPieces] = useState([]);
  const [showMessage, setShowMessage] = useState(false);

  useEffect(() => {
    if (!active) {
      setPieces([]);
      setShowMessage(false);
      return;
    }

    // Generate confetti pieces
    const newPieces = Array.from({ length: 80 }).map((_, i) => ({
      id: i,
      delay: Math.random() * 0.5,
      color: confettiColors[Math.floor(Math.random() * confettiColors.length)],
      shape: confettiShapes[Math.floor(Math.random() * confettiShapes.length)],
    }));
    
    setPieces(newPieces);
    setShowMessage(true);

    // Auto-complete after duration
    const timer = setTimeout(() => {
      setShowMessage(false);
      setTimeout(() => {
        setPieces([]);
        onComplete?.();
      }, 500);
    }, duration);

    return () => clearTimeout(timer);
  }, [active, duration, onComplete]);

  useEffect(() => {
    // Add confetti animation styles
    const styleId = "confetti-celebration-styles";
    if (document.getElementById(styleId)) return;

    const styleSheet = document.createElement("style");
    styleSheet.id = styleId;
    styleSheet.textContent = `
      @keyframes confettiFall {
        0% {
          transform: translateY(0) translateX(0) rotate(0deg) scale(1);
          opacity: 1;
        }
        100% {
          transform: translateY(60vh) translateX(var(--end-x)) rotate(var(--rotation)) scale(0.5);
          opacity: 0;
        }
      }
      
      @keyframes celebrationPop {
        0% {
          transform: scale(0) rotate(-10deg);
          opacity: 0;
        }
        50% {
          transform: scale(1.1) rotate(2deg);
        }
        100% {
          transform: scale(1) rotate(0deg);
          opacity: 1;
        }
      }
      
      @keyframes celebrationGlow {
        0%, 100% {
          box-shadow: 0 0 20px rgba(255, 215, 0, 0.3);
        }
        50% {
          box-shadow: 0 0 40px rgba(255, 215, 0, 0.6);
        }
      }
      
      @keyframes starBurst {
        0% {
          transform: scale(0) rotate(0deg);
          opacity: 1;
        }
        100% {
          transform: scale(2) rotate(180deg);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(styleSheet);

    return () => {
      const existing = document.getElementById(styleId);
      if (existing) existing.remove();
    };
  }, []);

  if (!active && pieces.length === 0) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-[9999]">
      {/* Confetti pieces */}
      {pieces.map((piece) => (
        <ConfettiPiece
          key={piece.id}
          delay={piece.delay}
          color={piece.color}
          shape={piece.shape}
        />
      ))}

      {/* Celebration message */}
      {showMessage && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div 
            className="bg-gradient-to-br from-amber-400 via-yellow-400 to-orange-400 text-white px-8 py-6 rounded-3xl shadow-2xl text-center pointer-events-auto"
            style={{
              animation: "celebrationPop 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards, celebrationGlow 1s ease-in-out infinite",
            }}
          >
            <div className="text-4xl mb-2">{message}</div>
            {subMessage && (
              <div className="text-lg text-amber-100 font-medium">{subMessage}</div>
            )}
            
            {/* Star bursts */}
            <div className="absolute -top-4 -left-4 text-3xl" style={{ animation: "starBurst 1s ease-out 0.3s forwards" }}>⭐</div>
            <div className="absolute -top-4 -right-4 text-3xl" style={{ animation: "starBurst 1s ease-out 0.5s forwards" }}>⭐</div>
            <div className="absolute -bottom-4 -left-4 text-2xl" style={{ animation: "starBurst 1s ease-out 0.4s forwards" }}>✨</div>
            <div className="absolute -bottom-4 -right-4 text-2xl" style={{ animation: "starBurst 1s ease-out 0.6s forwards" }}>✨</div>
          </div>
        </div>
      )}
    </div>
  );
}
